import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from '../components/RichTextEditor';
import {
  MEMBER_SECTIONS, adminListItems, adminCreateItem, adminUpdateItem, adminDeleteItem, adminDeleteFile,
  adminFileUrl, formatDate, formatSize,
} from '../utils/membersApi';
import { MEMBERS_PATH } from '../membersPath';
import './AdminMembers.css';

// The directory is managed in Outseta, so only content sections are editable here.
const CONTENT_SECTIONS = MEMBER_SECTIONS.filter((s) => s.id !== 'directory');

const GROUP_HINTS = {
  board: 'Optional, e.g. "Meeting reports" or "Statutes & rules"',
  conferences: 'The conference, e.g. "2026 – Olomouc, Czech Republic"',
  summercamps: 'The Summer Camp, e.g. "Summer Camp 2025"',
  meetlearn: 'Optional, e.g. "2026"',
};

const EMPTY_FORM = { group: '', title: '', date: '', description: '', lecturer: '', videoUrl: '' };

const AdminMembersDashboard = () => {
  const navigate = useNavigate();
  const hasToken = Boolean(sessionStorage.getItem('adminToken'));
  const [section, setSection] = useState('board');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);

  const handleError = useCallback((err) => {
    if (err.status === 401 || err.status === 403) {
      sessionStorage.removeItem('adminToken');
      navigate('/admin/members');
      return;
    }
    setMessage({ type: 'error', text: err.message || 'Something went wrong' });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await adminListItems(section));
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [section, handleError]);

  useEffect(() => {
    if (!hasToken) navigate('/admin/members');
  }, [hasToken, navigate]);

  useEffect(() => {
    if (hasToken) load();
  }, [hasToken, load]);

  const existingGroups = useMemo(
    () => [...new Set(items.map((i) => i.group).filter(Boolean))],
    [items],
  );

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFiles([]);
    setEditingId(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const switchSection = (id) => {
    setSection(id);
    resetForm();
    setMessage(null);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      group: item.group, title: item.title, date: item.date, description: item.description,
      lecturer: item.lecturer, videoUrl: item.videoUrl,
    });
    setFiles([]);
    if (fileInput.current) fileInput.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const fd = new FormData();
    if (!editingId) fd.append('section', section);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    files.forEach((f) => fd.append('files', f));
    try {
      if (editingId) await adminUpdateItem(editingId, fd);
      else await adminCreateItem(fd);
      setMessage({ type: 'success', text: editingId ? 'Item updated.' : 'Item added.' });
      resetForm();
      await load();
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  };

  const onDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}" and all its files?`)) return;
    try {
      await adminDeleteItem(item.id);
      if (editingId === item.id) resetForm();
      await load();
    } catch (err) {
      handleError(err);
    }
  };

  const onDeleteFile = async (item, file) => {
    if (!window.confirm(`Remove "${file.name}"?`)) return;
    try {
      await adminDeleteFile(item.id, file.id);
      await load();
    } catch (err) {
      handleError(err);
    }
  };

  const onPreview = async (item, file) => {
    const win = window.open('', '_blank');
    try {
      const url = await adminFileUrl(item.id, file.id);
      if (win) win.location.href = url;
    } catch (err) {
      if (win) win.close();
      handleError(err);
    }
  };

  const logout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin/members');
  };

  if (!hasToken) return null;

  const isMeetLearn = section === 'meetlearn';
  const sectionInfo = CONTENT_SECTIONS.find((s) => s.id === section);

  return (
    <div className="admin-members">
      <div className="admin-members-header">
        <div>
          <h1>Members area – content</h1>
          <p>Everything you add here is only visible to logged-in EBTA members.</p>
        </div>
        <div className="admin-members-header-actions">
          <a href={`${MEMBERS_PATH}/${section}`} target="_blank" rel="noopener noreferrer">View as member ↗</a>
          <button type="button" className="admin-members-secondary" onClick={logout}>Log out</button>
        </div>
      </div>

      <div className="admin-members-sections" role="tablist">
        {CONTENT_SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`admin-members-section-btn ${s.id === section ? 'active' : ''}`}
            onClick={() => switchSection(s.id)}
          >
            {s.short}
          </button>
        ))}
      </div>

      {message && <p className={`admin-members-message ${message.type}`}>{message.text}</p>}

      <form className="admin-members-form" onSubmit={onSubmit}>
        <h2>{editingId ? 'Edit item' : `Add to ${sectionInfo.short}`}</h2>

        <label htmlFor="am-title">Title *</label>
        <input id="am-title" name="title" value={form.title} onChange={onChange} required />

        <div className="admin-members-row">
          <div>
            <label htmlFor="am-date">Date</label>
            <input id="am-date" type="date" name="date" value={form.date} onChange={onChange} />
          </div>
          <div>
            <label htmlFor="am-group">Group</label>
            <input id="am-group" name="group" value={form.group} onChange={onChange} list="am-groups"
              placeholder={GROUP_HINTS[section]} />
            <datalist id="am-groups">
              {existingGroups.map((g) => <option key={g} value={g} />)}
            </datalist>
          </div>
        </div>

        {isMeetLearn && (
          <div className="admin-members-row">
            <div>
              <label htmlFor="am-lecturer">Guest lecturer</label>
              <input id="am-lecturer" name="lecturer" value={form.lecturer} onChange={onChange} />
            </div>
            <div>
              <label htmlFor="am-video">Video link (YouTube or Vimeo)</label>
              <input id="am-video" type="url" name="videoUrl" value={form.videoUrl} onChange={onChange}
                placeholder="https://vimeo.com/..." />
            </div>
          </div>
        )}

        <label>Description</label>
        <RichTextEditor
          value={form.description}
          onChange={(html) => setForm((f) => ({ ...f, description: html }))}
          placeholder="Optional text shown with this item"
        />

        <label htmlFor="am-files">Files (PDF, JPG, PNG or WebP, max 25 MB each)</label>
        <input
          id="am-files"
          ref={fileInput}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
        {files.length > 0 && <p className="admin-members-hint">{files.length} file(s) selected</p>}

        <div className="admin-members-form-actions">
          <button type="submit" className="admin-members-primary" disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Save changes' : 'Add item'}
          </button>
          {editingId && (
            <button type="button" className="admin-members-secondary" onClick={resetForm}>Cancel</button>
          )}
        </div>
      </form>

      <h2 className="admin-members-list-title">{sectionInfo.title}</h2>
      {loading && <p>Loading...</p>}
      {!loading && items.length === 0 && <p className="admin-members-hint">No items yet.</p>}
      <ul className="admin-members-list">
        {items.map((item) => (
          <li key={item.id} className={editingId === item.id ? 'editing' : ''}>
            <div className="admin-members-item-head">
              <div>
                <strong>{item.title}</strong>
                <span className="admin-members-item-meta">
                  {[item.group, formatDate(item.date), item.lecturer].filter(Boolean).join(' · ')}
                </span>
              </div>
              <div className="admin-members-item-actions">
                <button type="button" className="admin-members-secondary" onClick={() => startEdit(item)}>Edit</button>
                <button type="button" className="admin-members-danger" onClick={() => onDeleteItem(item)}>Delete</button>
              </div>
            </div>
            {item.videoUrl && <p className="admin-members-item-meta">🎥 {item.videoUrl}</p>}
            {item.files.length > 0 && (
              <ul className="admin-members-files">
                {item.files.map((f) => (
                  <li key={f.id}>
                    <button type="button" className="admin-members-link" onClick={() => onPreview(item, f)}>
                      {f.mimeType === 'application/pdf' ? '📄' : '🖼️'} {f.name}
                    </button>
                    <span className="admin-members-item-meta">{formatSize(f.size)}</span>
                    <button
                      type="button"
                      className="admin-members-remove"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => onDeleteFile(item, f)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AdminMembersDashboard;
